import type { ReactNode, RefObject } from 'react';

import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { getDefaultImageProperties, getTestProps } from '../../lib/helpers';

import Loader from '../Loader/Loader';

interface CardProps {
  /**
   * Forwarded ref to the card element
   */
  ref?: RefObject<HTMLDivElement>;
  /**
   * Image url
   */
  imageUrl?: string;
  /**
   * Image alt attribute
   */
  imageUrlAlt?: string;
  /**
   * If image url is loading
   */
  imageUrlLoading?: boolean;
  /**
   * Image thumbnail if you want to use different than imageUrl
   */
  thumbnail?: ReactNode;
  /**
   * Body content
   */
  body?: ReactNode;
  /**
   * Image overlay content
   */
  imageOverlay?: ReactNode;
  /**
   * On card click callback
   */
  onClick?: () => void;
  /**
   * Card link
   */
  link?: string;
  /**
   * If body should be placed next to the image
   */
  horizontal?: boolean;
  /**
   * If card should be darkened on hover
   */
  darkenOnHover?: boolean;
  /**
   * Additional card container classes
   */
  additionalContainerClasses?: string;
  /**
   * Additional image container classes
   */
  additionalImageContainerClasses?: string;
  /**
   * Additional image classes
   */
  additionalImageClasses?: string;
  /**
   * Additional body classes
   */
  additionalBodyClasses?: string;
  /**
   * Additional image thumbnail classes
   */
  additionalThumbnailClasses?: string;
  /**
   * Test id of the card
   */
  testId?: string;
}

const Card = ({
  ref,
  imageUrl = '',
  imageUrlAlt = '',
  imageUrlLoading = false,
  thumbnail = '',
  body = '',
  imageOverlay = '',
  onClick,
  link,
  horizontal = false,
  darkenOnHover = true,
  additionalContainerClasses,
  additionalImageContainerClasses,
  additionalImageClasses,
  additionalBodyClasses,
  additionalThumbnailClasses,
  testId,
}: CardProps) => {
  const isInteractive = !!onClick || !!link;

  return (
    <div
      ref={ref}
      className={twMerge(
        'w-fit h-fit transition-all duration-300 relative overflow-hidden',
        'items-center justify-center group rounded-lg',
        horizontal ? 'inline-flex' : 'flex-col',
        isInteractive && 'cursor-pointer hover:shadow-xl',
        additionalContainerClasses,
      )}
      {...getTestProps(testId, 'card-container')}
    >
      {link && (
        <Link
          to={link}
          className="absolute inset-0 z-1 rounded-lg"
          onClick={onClick}
          {...getTestProps(testId, 'card-link')}
        />
      )}
      {!link && onClick && (
        <button
          type="button"
          className="absolute inset-0 z-1 cursor-pointer rounded-lg"
          onClick={onClick}
          {...getTestProps(testId, 'card-button')}
        />
      )}
      <div
        className={twMerge(
          'group flex w-full h-full bg-slate-100 overflow-hidden justify-center items-center',
          'relative',
          additionalImageContainerClasses,
        )}
        {...getTestProps(testId, 'image-container')}
      >
        {imageUrlLoading && (
          <div
            className="flex absolute inset-0 bg-indigo-950 opacity-60"
            {...getTestProps(testId, 'image-loading')}
          >
            <Loader type="spinner-white" additionalClasses="m-auto" />
          </div>
        )}
        {darkenOnHover && (
          <div
            className="transition-opacity duration-300 ease-in-out absolute h-full
                w-full bg-indigo-950 opacity-0 group-hover:opacity-60"
            {...getTestProps(testId, 'image-darken')}
          />
        )}
        {imageOverlay && (
          <div
            className={twMerge(
              'absolute h-full w-full z-2',
              isInteractive && 'pointer-events-none',
            )}
            {...getTestProps(testId, 'image-overlay')}
          >
            {imageOverlay}
          </div>
        )}

        {imageUrl && (
          <img
            key={imageUrl}
            src={imageUrl}
            alt={imageUrlAlt}
            className={twMerge(
              'w-full h-full object-center object-cover',
              additionalImageClasses,
            )}
            {...getDefaultImageProperties()}
            {...getTestProps(testId, 'image')}
          />
        )}

        {thumbnail && (
          <div
            className={twMerge(
              'h-full w-full',
              isInteractive && 'pointer-events-none',
              additionalThumbnailClasses,
            )}
            {...getTestProps(testId, 'thumbnail')}
          >
            {thumbnail}
          </div>
        )}
      </div>
      {body && (
        <div
          className={twMerge(
            'relative z-2',
            isInteractive && 'pointer-events-none',
            additionalBodyClasses,
          )}
          {...getTestProps(testId, 'body')}
        >
          {body}
        </div>
      )}
    </div>
  );
};

export default Card;
